if (!customElements.get("m-slideshow")) {
  class MSlideshow extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // a11y (2.2.2, 2.3.3): no auto-rotation for visitors who ask for reduced motion
      this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.singleSlide = this.querySelectorAll(".swiper-slide").length < 2;
      this.autoplay = this.dataset.autoplay === "true" && !this.prefersReducedMotion && !this.singleSlide;
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed);
      this.showArrows = this.dataset.enableArrows === "true";
      this.showDots = this.dataset.enableDots === "true";
      this.adaptHeight = this.dataset.slideHeight === "adapt";
      this.id = this.dataset.id;
      this.sliderContainer = this.querySelector(".swiper-container");
      this.pauseButton = this.querySelector("[data-slideshow-pause]");
      this.userPaused = this.dataset.autoplay === "true" && this.prefersReducedMotion;

      this.lastVideo = null;
      this.lastActive = null;
      this.timeout = null;
      this.initSlider();
      this.initA11y();
    }

    initSlider() {
      this.slider = new MinimogLibs.Swiper(this.sliderContainer, {
        init: false,
        autoplay: this.autoplay
          ? {
              delay: this.autoplaySpeed * 1000,
              disableOnInteraction: true,
            }
          : false,
        slidesPerView: 1,
        slidesPerGroup: 1,
        loop: !this.singleSlide,
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        speed: 1000,
        navigation: this.showArrows
          ? {
              nextEl: this.querySelector(".m-slider-controls__button-next"),
              prevEl: this.querySelector(".m-slider-controls__button-prev"),
            }
          : false,
        pagination: this.showDots
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              bulletClass: "m-dot",
              bulletActiveClass: "m-dot--active",
            }
          : false,
        breakpoints: {
          992: {
            threshold: 2,
          },
        },
        on: {
          init() {
            setTimeout(() => {
              window.dispatchEvent(new Event("resize"));
            }, 100);
          },
        },
      });

      this.lastSlide = null;
      this.currentSlide = null;
      this.slider && this.slider.on("init", this.handleChange.bind(this));
      this.slider && this.slider.on("slideChange", this.handleChange.bind(this));
      this.slider && this.slider.on("slideChangeTransitionEnd", this.updateSlideA11y.bind(this));
      this.slider && this.slider.on("paginationUpdate", this.updatePaginationA11y.bind(this));
      this.slider.init();

      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (e) => this.onBlockSelect(e));
      }
    }

    handleChange(swiper) {
      const { activeIndex, slides } = swiper;
      const slideType = slides[activeIndex] && slides[activeIndex].dataset.slideType;
      this.lastActive && this.slider.slideToLoop(this.lastActive);
      if (slideType === "video_slide") {
        const video = slides[activeIndex] && slides[activeIndex].querySelector("video");

        if (video) {
          let playPromise = video.play();
          this.lastVideo && this.lastVideo.pause();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              this.lastVideo = video;
            });
          }
        }
      } else {
        this.lastVideo && this.lastVideo.pause();
        this.lastVideo = null;
      }
      this.updateSlideA11y();
    }

    onBlockSelect(ev) {
      const block = ev.target;
      const index = Number(block.dataset.slide);
      this.lastActive = index;
      if (this.slider) {
        this.slider.slideToLoop(index);
      }
    }

    /* ---------------- Accessibility ---------------- */

    initA11y() {
      if (this.pauseButton) {
        this.setPausedState(this.userPaused);
        this.pauseButton.addEventListener("click", () => {
          this.userPaused = !this.userPaused;
          if (this.userPaused) this.stopAutoplay();
          else this.startAutoplay();
          this.setPausedState(this.userPaused);
        });
      }

      // Pause while the pointer or keyboard focus is on slide content; resume unless the user paused.
      // Focus on the Pause/Play button itself does not count, so Play takes effect immediately.
      const pauseTemporarily = (e) => {
        if (e && e.type === "focusin" && e.target === this.pauseButton) return;
        this.stopAutoplay();
      };
      const resume = () => {
        if (!this.userPaused && !this.contains(document.activeElement) && !this.matches(":hover")) this.startAutoplay();
      };
      this.addEventListener("mouseenter", pauseTemporarily);
      this.addEventListener("mouseleave", resume);
      this.addEventListener("focusin", pauseTemporarily);
      this.addEventListener("focusout", () => setTimeout(resume, 0));

      this.updateSlideA11y();
      this.updatePaginationA11y();
    }

    startAutoplay() {
      if (!this.slider || !this.slider.autoplay || this.dataset.autoplay !== "true") return;
      if (!this.slider.params.autoplay) {
        this.slider.params.autoplay = { delay: this.autoplaySpeed * 1000, disableOnInteraction: true };
      }
      this.slider.autoplay.start();
    }

    stopAutoplay() {
      this.slider && this.slider.autoplay && this.slider.autoplay.running && this.slider.autoplay.stop();
    }

    // The label reflects the user's choice, not temporary hover/focus pauses
    setPausedState(paused) {
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute("aria-label", paused ? "Play slideshow" : "Pause slideshow");
      this.pauseButton.classList.toggle("is-paused", paused);
    }

    // Only the visible slide is reachable; hidden and loop-duplicate slides are inert (2.4.3, 4.1.2)
    updateSlideA11y() {
      if (!this.slider || !this.slider.slides) return;
      const active = this.slider.slides[this.slider.activeIndex];
      Array.prototype.forEach.call(this.slider.slides, (slide) => {
        const isActive = slide === active;
        slide.inert = !isActive;
        slide.setAttribute("aria-hidden", String(!isActive));
      });
    }

    // Pagination dots become real, labelled buttons
    updatePaginationA11y() {
      const bullets = this.querySelectorAll(".swiper-pagination .m-dot");
      Array.prototype.forEach.call(bullets, (bullet, index) => {
        if (!bullet.hasAttribute("role")) {
          bullet.setAttribute("role", "button");
          bullet.setAttribute("tabindex", "0");
          bullet.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              bullet.click();
            }
          });
        }
        bullet.setAttribute("aria-label", `Go to slide ${index + 1}`);
        if (bullet.classList.contains("m-dot--active")) bullet.setAttribute("aria-current", "true");
        else bullet.removeAttribute("aria-current");
      });
    }
  }

  customElements.define("m-slideshow", MSlideshow);
}
